(() => {
  const neo_desktop_media = window.matchMedia('(min-width: 990px)');
  const neo_reduced_motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const neo_selector = '.lookbook__wrapper[data-neo-infinite-scroll="true"]';
  const neo_states = new Map();

  const neo_tryPlay = (video) => {
    const playPromise = video.play();
    playPromise?.catch(() => {});
  };

  const neo_disableCloneInteraction = (clone) => {
    clone.dataset.neoLookbookClone = 'true';
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');
    clone.removeAttribute('data-shopify-editor-block');

    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('video').forEach((video) => {
      video.removeAttribute('autoplay');
      video.autoplay = false;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.pause();
    });
    clone
      .querySelectorAll('a, button, input, select, textarea, video, audio, iframe, [tabindex]')
      .forEach((element) => element.setAttribute('tabindex', '-1'));
  };

  const neo_syncCloneVideo = (master, clone, force = false) => {
    const sync = () => {
      if (
        Number.isFinite(master.duration) &&
        Number.isFinite(clone.duration) &&
        clone.duration > 0 &&
        Math.abs(clone.currentTime - master.currentTime) > (force ? 0.05 : 0.35)
      ) {
        clone.currentTime = master.currentTime % clone.duration;
      }

      if (master.paused || master.ended) {
        clone.pause();
      } else {
        neo_tryPlay(clone);
      }
    };

    if (clone.readyState >= 1) {
      sync();
    } else {
      clone.addEventListener('loadedmetadata', sync, { once: true });
      clone.load();
    }
  };

  const neo_prepareVideoGroups = (state) => {
    state.mediaObserver?.disconnect();
    state.cloneGroups.clear();
    state.visibleClones.clear();

    state.mediaObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const clone = entry.target;
          const master = state.cloneMasters.get(clone);
          if (!master) return;

          if (entry.isIntersecting && state.isVisible) {
            state.visibleClones.add(clone);
            neo_syncCloneVideo(master, clone, true);
          } else {
            state.visibleClones.delete(clone);
            clone.pause();
          }
        });
      },
      {
        root: state.wrapper,
        rootMargin: '0px 50% 0px 50%',
        threshold: 0.01,
      }
    );

    state.clones.forEach((clone) => {
      const sourceIndex = Number(clone.dataset.neoLookbookSourceIndex);
      const masterVideos = state.slides[sourceIndex]?.querySelectorAll('video') || [];

      clone.querySelectorAll('video').forEach((cloneVideo, videoIndex) => {
        const master = masterVideos[videoIndex];
        if (!master) return;

        cloneVideo.removeAttribute('autoplay');
        cloneVideo.autoplay = false;
        cloneVideo.muted = true;
        cloneVideo.defaultMuted = true;
        cloneVideo.playsInline = true;
        cloneVideo.setAttribute('muted', '');
        cloneVideo.setAttribute('playsinline', '');
        cloneVideo.preload = 'metadata';
        cloneVideo.pause();

        if (!state.cloneGroups.has(master)) {
          state.cloneGroups.set(master, new Set());
        }

        state.cloneGroups.get(master).add(cloneVideo);
        state.cloneMasters.set(cloneVideo, master);
        state.mediaObserver.observe(cloneVideo);
      });
    });
  };

  const neo_pauseAllVideos = (state) => {
    state.masterVideos.forEach((video) => video.pause());
    state.visibleClones.forEach((video) => video.pause());
  };

  const neo_playAutoplayMasters = (state) => {
    state.masterVideos.forEach((video) => {
      if (video.closest('.autoplay')) {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        neo_tryPlay(video);
      }
    });
  };

  const neo_stopAutoSlide = (state) => {
    clearTimeout(state.autoTimer);
    state.autoTimer = 0;
    state.autoActive = false;
  };

  const neo_startRender = (state) => {
    if (state.frame) return;
    state.lastFrameTime = performance.now();
    state.frame = requestAnimationFrame((time) => neo_render(state, time));
  };

  const neo_scheduleAutoSlide = (state) => {
    neo_stopAutoSlide(state);
    if (!state.autoEnabled || !state.isVisible) return;

    state.autoTimer = window.setTimeout(() => {
      state.autoActive = true;
      neo_startRender(state);
    }, state.autoDelay);
  };

  const neo_render = (state, time) => {
    state.frame = 0;
    const elapsed = Math.min(64, time - state.lastFrameTime);
    const elapsedSeconds = elapsed / 1000;
    state.lastFrameTime = time;

    if (state.autoActive && state.isVisible) {
      const desiredVelocity = state.autoSpeed;
      const autoBlend = 1 - Math.exp(-3.5 * elapsedSeconds);
      state.velocity += (desiredVelocity - state.velocity) * autoBlend;
    } else {
      state.velocity *= Math.exp(-5.5 * elapsedSeconds);

      if (Math.abs(state.velocity) < 0.5) {
        state.velocity = 0;
      }
    }

    state.target += state.velocity * elapsedSeconds;

    const difference = state.target - state.current;
    const followStrength = neo_reduced_motion.matches
      ? 1
      : 1 - Math.pow(0.86, elapsed / (1000 / 60));
    state.current += difference * followStrength;

    if (Math.abs(difference) < 0.1) {
      state.current = state.target;
    }

    if (state.cycleWidth > 0) {
      const wrappedPosition = gsap.utils.wrap(0, state.cycleWidth, state.current);
      gsap.set(state.track, { x: -wrappedPosition });
    }

    if (
      (state.autoActive && state.isVisible) ||
      Math.abs(state.velocity) >= 0.5 ||
      Math.abs(state.target - state.current) >= 0.1
    ) {
      neo_startRender(state);
    }
  };

  const neo_removeClones = (state) => {
    state.mediaObserver?.disconnect();
    state.visibleClones.forEach((video) => video.pause());
    state.visibleClones.clear();
    state.cloneGroups.clear();
    state.cloneMasters = new WeakMap();
    state.clones.forEach((clone) => clone.remove());
    state.clones = [];
  };

  const neo_destroyDesktopLoop = (wrapper) => {
    const state = neo_states.get(wrapper);
    if (!state) return;

    wrapper.removeEventListener('wheel', state.onWheel);
    document.removeEventListener('visibilitychange', state.onVisibilityChange);
    state.resizeObserver.disconnect();
    state.visibilityObserver.disconnect();
    state.masterCleanups.forEach((cleanup) => cleanup());
    neo_stopAutoSlide(state);
    cancelAnimationFrame(state.resizeFrame);
    cancelAnimationFrame(state.frame);
    neo_removeClones(state);
    gsap.set(state.track, { clearProps: 'transform' });
    delete wrapper.dataset.neoInfiniteInitialized;
    neo_states.delete(wrapper);
  };

  const neo_createDesktopLoop = (wrapper) => {
    if (
      !neo_desktop_media.matches ||
      neo_states.has(wrapper) ||
      wrapper.dataset.neoInfiniteInitialized === 'true'
    ) {
      return;
    }

    const track = wrapper.querySelector('.lookbook__slides');
    const slides = Array.from(track?.children || []).filter(
      (element) =>
        element.classList.contains('lookbook__slide') &&
        element.dataset.neoLookbookClone !== 'true'
    );

    if (!track || slides.length < 2) return;

    const state = {
      wrapper,
      track,
      slides,
      clones: [],
      cloneGroups: new Map(),
      cloneMasters: new WeakMap(),
      visibleClones: new Set(),
      masterVideos: slides.flatMap((slide) => Array.from(slide.querySelectorAll('video'))),
      masterCleanups: [],
      mediaObserver: null,
      visibilityObserver: null,
      cycleWidth: 0,
      current: 0,
      target: 0,
      velocity: 0,
      frame: 0,
      resizeFrame: 0,
      autoTimer: 0,
      autoActive: false,
      autoEnabled:
        wrapper.dataset.neoAutoSlide === 'true' && !neo_reduced_motion.matches,
      autoDelay: Number(wrapper.dataset.neoAutoSlideDelay) || 3000,
      autoSpeed: Number(wrapper.dataset.neoAutoSlideSpeed) || 30,
      isVisible: false,
      lastFrameTime: performance.now(),
      lastWheelTime: 0,
      onWheel: null,
      onVisibilityChange: null,
      resizeObserver: null,
    };

    state.masterVideos.forEach((master) => {
      const syncVisibleClones = (force = false) => {
        state.cloneGroups.get(master)?.forEach((clone) => {
          if (state.visibleClones.has(clone)) {
            neo_syncCloneVideo(master, clone, force);
          }
        });
      };
      const onPlay = () => syncVisibleClones(true);
      const onPause = () => {
        state.cloneGroups.get(master)?.forEach((clone) => clone.pause());
      };
      const onTimeUpdate = () => syncVisibleClones(false);

      master.addEventListener('play', onPlay);
      master.addEventListener('pause', onPause);
      master.addEventListener('timeupdate', onTimeUpdate);
      state.masterCleanups.push(() => {
        master.removeEventListener('play', onPlay);
        master.removeEventListener('pause', onPause);
        master.removeEventListener('timeupdate', onTimeUpdate);
      });
    });

    const neo_measureAndClone = () => {
      cancelAnimationFrame(state.resizeFrame);
      state.resizeFrame = requestAnimationFrame(() => {
        neo_removeClones(state);
        gsap.set(track, { x: 0 });

        state.cycleWidth = slides.reduce(
          (width, slide) => width + slide.getBoundingClientRect().width,
          0
        );
        if (state.cycleWidth <= 0) return;

        const cloneSetCount = Math.max(
          1,
          Math.ceil(wrapper.clientWidth / state.cycleWidth)
        );

        for (let copyIndex = 0; copyIndex < cloneSetCount; copyIndex += 1) {
          slides.forEach((slide, sourceIndex) => {
            const clone = slide.cloneNode(true);
            neo_disableCloneInteraction(clone);
            clone.dataset.neoLookbookSourceIndex = String(sourceIndex);
            track.appendChild(clone);
            state.clones.push(clone);
          });
        }

        neo_prepareVideoGroups(state);
        state.current = gsap.utils.wrap(0, state.cycleWidth, state.current);
        state.target = state.current;
        gsap.set(track, { x: -state.current });
        neo_startRender(state);
      });
    };

    state.onWheel = (event) => {
      if (state.cycleWidth <= 0) return;

      event.preventDefault();
      neo_scheduleAutoSlide(state);
      neo_playAutoplayMasters(state);
      const deltaScale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? wrapper.clientWidth : 1;
      const wheelDelta = Math.max(
        -160,
        Math.min(160, (event.deltaY + event.deltaX) * deltaScale)
      );
      const eventTime = performance.now();
      const wheelElapsed = state.lastWheelTime
        ? Math.max(8, Math.min(80, eventTime - state.lastWheelTime))
        : 16;
      const wheelVelocity = Math.max(
        -2200,
        Math.min(2200, wheelDelta / (wheelElapsed / 1000))
      );

      state.lastWheelTime = eventTime;
      state.velocity = state.velocity * 0.35 + wheelVelocity * 0.65;
      state.target += wheelDelta;

      if (Math.abs(state.target) > state.cycleWidth * 1000) {
        const normalizedTarget = gsap.utils.wrap(0, state.cycleWidth, state.target);
        const offset = state.target - normalizedTarget;
        state.target -= offset;
        state.current -= offset;
      }

      neo_startRender(state);
    };

    state.onVisibilityChange = () => {
      if (document.hidden) {
        neo_stopAutoSlide(state);
        neo_pauseAllVideos(state);
      } else if (state.isVisible) {
        neo_playAutoplayMasters(state);
        neo_scheduleAutoSlide(state);
      }
    };

    state.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        state.isVisible = entry.isIntersecting;

        if (state.isVisible && !document.hidden) {
          neo_playAutoplayMasters(state);
          neo_scheduleAutoSlide(state);
        } else {
          neo_stopAutoSlide(state);
          neo_pauseAllVideos(state);
        }
      },
      { threshold: 0.01 }
    );

    state.resizeObserver = new ResizeObserver(neo_measureAndClone);
    state.resizeObserver.observe(wrapper);
    state.visibilityObserver.observe(wrapper);
    wrapper.addEventListener('wheel', state.onWheel, { passive: false });
    document.addEventListener('visibilitychange', state.onVisibilityChange);
    wrapper.dataset.neoInfiniteInitialized = 'true';
    neo_states.set(wrapper, state);

    neo_measureAndClone();
  };

  const neo_syncLoops = (scope = document) => {
    const wrappers = scope.matches?.(neo_selector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(neo_selector) || []);

    wrappers.forEach((wrapper) => {
      if (neo_desktop_media.matches) {
        neo_createDesktopLoop(wrapper);
      } else {
        neo_destroyDesktopLoop(wrapper);
      }
    });
  };

  neo_syncLoops();

  neo_desktop_media.addEventListener('change', () => neo_syncLoops());

  document.addEventListener('shopify:section:load', (event) => {
    neo_syncLoops(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(neo_selector).forEach(neo_destroyDesktopLoop);
  });
})();
